import React, { useEffect, useRef, useState } from 'react';

const experiences = [
    {
        number: '01',
        label: 'FOCUS',
        title: 'Deep in a task',
        description:
            'Your device recognizes that you’re focused — and gives you the space to stay there.',
        color: '#5b8cff',
        glow: 'rgba(91,140,255,0.32)',
        points:
            '0,54 18,51 36,49 54,45 72,39 90,42 108,34 126,38 144,29 162,33 180,27 198,31 216,24 234,29 252,22',
    },
    {
        number: '02',
        label: 'EFFORT',
        title: 'Pushing too hard',
        description:
            'It recognizes when mental effort is building — and can help you know when to ease off.',
        color: '#9b7cff',
        glow: 'rgba(155,124,255,0.30)',
        points:
            '0,55 18,53 36,49 54,50 72,44 90,42 108,38 126,40 144,34 162,35 180,30 198,32 216,26 234,28 252,21',
    },
    {
        number: '03',
        label: 'READINESS',
        title: 'Ready to learn',
        description:
            'It recognizes when your mind is ready to take in something new.',
        color: '#48d8e8',
        glow: 'rgba(72,216,232,0.28)',
        points:
            '0,48 18,39 36,45 54,36 72,42 90,31 108,39 126,34 144,41 162,29 180,35 198,26 216,32 234,23 252,29',
    },
    {
        number: '04',
        label: 'RECOVERY',
        title: 'Time for a break',
        description:
            'It recognizes when you’ve been pushing long enough — and gives you space to recover.',
        color: '#5b8cff',
        glow: 'rgba(91,140,255,0.32)',
        points:
            '0,24 18,27 36,29 54,32 72,34 90,37 108,39 126,42 144,44 162,47 180,49 198,51 216,54 234,56 252,59',
    },
];

const SignalGraph = ({
                         points,
                         color,
                         glow,
                         progress,
                         isHovered,
                     }) => {
    const signalRef = useRef(null);
    const [dotPosition, setDotPosition] = useState({ x: 0, y: 54 });

    /*
     * Update the glowing signal point so it travels with
     * the graph as the signal grows.
     */
    useEffect(() => {
        const signal = signalRef.current;

        if (!signal) {
            return;
        }

        try {
            const totalLength = signal.getTotalLength();

            if (totalLength === 0) {
                return;
            }

            const point = signal.getPointAtLength(
                totalLength * (progress / 100)
            );

            setDotPosition({
                x: point.x,
                y: point.y,
            });
        } catch {
            // Ignore unsupported SVG geometry calculations.
        }
    }, [progress]);

    return (
        <div className="relative mt-7 h-[105px] w-full overflow-hidden">

            {/* Baseline */}
            <div className="absolute bottom-[14px] left-0 right-0 h-px bg-white/[0.07]" />

            {/* Secondary guide line */}
            <div className="absolute bottom-[52px] left-0 right-0 h-px bg-white/[0.035]" />

            <svg
                viewBox="0 0 252 70"
                preserveAspectRatio="none"
                className="absolute inset-x-0 bottom-[14px] h-[70px] w-full overflow-visible"
            >

                {/* Soft glow behind signal */}
                <polyline
                    points={points}
                    fill="none"
                    stroke={color}
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    pathLength="100"
                    strokeDasharray={`${progress} 100`}
                    strokeDashoffset="0"
                    opacity={isHovered ? 0.14 : 0.025}
                    style={{
                        filter: isHovered
                            ? `drop-shadow(0 0 10px ${glow})`
                            : 'none',
                    }}
                />

                {/* Main growing signal */}
                <polyline
                    ref={signalRef}
                    points={points}
                    fill="none"
                    stroke={color}
                    strokeWidth={isHovered ? '1.8' : '1.2'}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    pathLength="100"
                    strokeDasharray={`${progress} 100`}
                    strokeDashoffset="0"
                    opacity={isHovered ? 0.95 : 0.16}
                />

                {/* Moving signal point */}
                {isHovered && progress > 0 && (
                    <circle
                        cx={dotPosition.x}
                        cy={dotPosition.y}
                        r={progress >= 75 ? '3' : '2'}
                        fill={color}
                        opacity="0.95"
                        style={{
                            filter: `drop-shadow(0 0 7px ${glow})`,
                        }}
                    />
                )}

            </svg>
        </div>
    );
};

const Experience = () => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [progress, setProgress] = useState(0);

    const animationRef = useRef(null);

    /*
     * Start the signal animation when the cursor enters a card.
     */
    const startAnimation = (index) => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }

        setHoveredIndex(index);
        setProgress(0);

        const startTime = performance.now();

        const duration = 2600;

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;

            const rawProgress = Math.min(
                elapsed / duration,
                1
            );

            /*
             * Ease-out curve:
             * starts quickly and gradually slows near 100%.
             */
            const easedProgress =
                1 - Math.pow(1 - rawProgress, 2.2);

            setProgress(easedProgress * 100);

            if (rawProgress < 1) {
                animationRef.current =
                    requestAnimationFrame(animate);
            }
        };

        animationRef.current =
            requestAnimationFrame(animate);
    };

    /*
     * Stop and reset when the cursor leaves.
     */
    const stopAnimation = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }

        setProgress(0);
        setHoveredIndex(null);
    };

    /*
     * Clean up animation if component unmounts.
     */
    useEffect(() => {
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    return (
        <section
            id="experience"
            className="relative overflow-hidden bg-[#0b0d14] py-20 text-white sm:py-24"
        >

            {/* Background atmosphere */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/2 top-1/3 h-[600px] w-[900px] -translate-x-1/2 rounded-full bg-[#3157ff]/[0.025] blur-[170px]" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1400px] px-8">

                {/* Heading */}
                <div className="max-w-[900px]">

                    <div className="mb-6 flex items-center gap-3 text-[11px] tracking-[0.28em] text-neutral-500">
                        <span className="h-px w-8 bg-neutral-600" />
                        THE EXPERIENCE
                    </div>

                    <h2 className="text-[42px] font-semibold leading-[1.02] tracking-[-0.04em] sm:text-[54px] lg:text-[64px]">

                        <span className="text-neutral-500">
                            Imagine if your devices
                        </span>

                        <br />

                        <span className="text-white">
                            understood what you needed.
                        </span>

                    </h2>

                    <p className="mt-6 max-w-[720px] text-[16px] leading-[1.7] text-neutral-400 sm:text-[17px]">
                        Your mind does not work the same way all day.
                        FroCort is building toward devices that can recognize
                        those changes and respond in ways that feel more
                        useful, timely and personal.
                    </p>

                </div>

                {/* Experience cards */}
                <div className="mt-14 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">

                    {experiences.map((experience, index) => {

                        const isHovered = hoveredIndex === index;

                        /*
                         * The experience becomes "understood"
                         * only after the signal crosses 75%.
                         */
                        const isUnderstood =
                            isHovered && progress >= 75;

                        return (
                            <article
                                key={experience.number}
                                onMouseEnter={() =>
                                    startAnimation(index)
                                }
                                onMouseLeave={stopAnimation}
                                className={`
                                    group relative flex min-h-[430px]
                                    flex-col overflow-hidden
                                    rounded-[24px] border p-6 sm:p-7
                                    cursor-default
                                    transition-all duration-500
                                    ${
                                    isHovered
                                        ? 'bg-[#0d1220]'
                                        : 'bg-[#0b0d14]'
                                }
                                `}
                                style={{
                                    borderColor: isHovered
                                        ? `${experience.color}80`
                                        : 'rgba(255,255,255,0.10)',

                                    boxShadow: isHovered
                                        ? `0 0 50px ${experience.glow.replace(
                                            '0.32',
                                            '0.08'
                                        )}`
                                        : 'none',
                                }}
                            >

                                {/* Top row */}
                                <div className="flex items-center justify-between">

                                    <div
                                        className={`
                                            text-[10px]
                                            tracking-[0.24em]
                                            transition-colors
                                            duration-500
                                            ${
                                            isHovered
                                                ? 'text-neutral-300'
                                                : 'text-neutral-600'
                                        }
                                        `}
                                    >
                                        {experience.number}
                                    </div>

                                    <div className="flex items-center gap-2">

                                        <span
                                            className={`
                                                text-[9px]
                                                tracking-[0.18em]
                                                transition-colors
                                                duration-500
                                                ${
                                                isHovered
                                                    ? 'text-neutral-400'
                                                    : 'text-neutral-700'
                                            }
                                            `}
                                        >
                                            {isHovered
                                                ? 'LIVE SIGNAL'
                                                : 'HOVER TO EXPLORE'}
                                        </span>

                                        <span
                                            className={`
                                                h-1.5 w-1.5 rounded-full
                                                transition-all duration-500
                                            `}
                                            style={{
                                                backgroundColor: isHovered
                                                    ? experience.color
                                                    : '#404040',

                                                boxShadow: isHovered
                                                    ? `0 0 10px ${experience.glow}`
                                                    : 'none',
                                            }}
                                        />

                                    </div>

                                </div>

                                {/* Cognitive category */}
                                <div
                                    className={`
                                        mt-9 text-[10px]
                                        tracking-[0.24em]
                                        transition-colors duration-500
                                        ${
                                        isHovered
                                            ? 'text-neutral-300'
                                            : 'text-neutral-700'
                                    }
                                    `}
                                >
                                    {experience.label}
                                </div>

                                {/* Graph */}
                                <SignalGraph
                                    points={experience.points}
                                    color={experience.color}
                                    glow={experience.glow}
                                    progress={
                                        isHovered ? progress : 0
                                    }
                                    isHovered={isHovered}
                                />

                                {/* Content */}
                                <div className="mt-auto">

                                    {/* Experience title */}
                                    <h3
                                        className={`
                                            text-[24px]
                                            font-medium
                                            tracking-[-0.025em]
                                            transition-all
                                            duration-700
                                        `}
                                        style={{
                                            color: isUnderstood
                                                ? '#ffffff'
                                                : isHovered
                                                    ? '#737780'
                                                    : '#737780',

                                            textShadow: isUnderstood
                                                ? `0 0 24px ${experience.glow}`
                                                : 'none',

                                            transform: isUnderstood
                                                ? 'translateY(-2px)'
                                                : 'translateY(0)',
                                        }}
                                    >
                                        {experience.title}
                                    </h3>

                                    {/* Description */}
                                    <p
                                        className={`
                                            mt-3 max-w-[360px]
                                            text-[14px]
                                            leading-[1.65]
                                            transition-colors
                                            duration-700
                                        `}
                                        style={{
                                            color: isUnderstood
                                                ? '#aeb4c0'
                                                : '#525762',
                                        }}
                                    >
                                        {experience.description}
                                    </p>

                                </div>

                                {/* Progress indicator */}
                                <div className="mt-7 flex items-center gap-1.5">

                                    {[0, 1, 2].map((dot) => {

                                        const activeDot =
                                            isHovered &&
                                            (
                                                (dot === 0 &&
                                                    progress > 0) ||
                                                (dot === 1 &&
                                                    progress >= 40) ||
                                                (dot === 2 &&
                                                    progress >= 75)
                                            );

                                        return (
                                            <span
                                                key={dot}
                                                className="h-1.5 w-1.5 rounded-full transition-all duration-300"
                                                style={{
                                                    backgroundColor:
                                                        activeDot
                                                            ? experience.color
                                                            : '#272b34',

                                                    boxShadow:
                                                        activeDot
                                                            ? `0 0 8px ${experience.glow}`
                                                            : 'none',
                                                }}
                                            />
                                        );
                                    })}

                                </div>

                            </article>
                        );
                    })}

                </div>

                {/* Product philosophy */}
                <div className="mt-12 grid items-end gap-8 border-t border-white/10 pt-7 lg:grid-cols-[1fr_auto]">

                    <p className="max-w-[900px] text-[22px] font-medium leading-[1.4] tracking-[-0.02em] text-neutral-500 sm:text-[28px]">

                        <span className="text-neutral-500">
                            The goal isn't another dashboard.
                        </span>

                        {' '}

                        <span className="text-white">
                            It's a device that responds to you.
                        </span>

                    </p>

                    <p className="max-w-[330px] text-[12px] leading-5 text-neutral-600 lg:text-right">
                        FroCort is building toward these experiences.
                        The specific cognitive signals, endpoints and user
                        value will be validated through human studies.
                    </p>

                </div>

            </div>

        </section>
    );
};

export default Experience;